/**
 * Shared Projects form modal for creating and editing JobNumber-backed projects.
 *
 * Used from Projects Home (add + edit) and the Project page (edit). Parents pass
 * ``mode`` (``add`` | ``edit``) and an ``onSubmit`` handler for create vs patch.
 */

import { useEffect, useMemo, useState } from "react";

import {
  Button,
  ModalWrapper,
  SearchableDropdown,
  SearchableMultiSelect,
} from "../../../components/ui";
import { X } from "../../../components/icons/svg/outline";
import { getAddresses, getCustomers, getUsersByRole } from "../../../services/api/core";
import { getProjectTitle } from "../../../utils/business";
import { getUserDisplayName } from "../../../utils/business/user";

const TITLE_ID = "project-form-modal-title";

/**
 * Build the initial modal draft from a project API row (empty for add mode).
 *
 * @param {Object|null} project - Project API row.
 * @returns {Object}
 */
function getInitialDraft(project) {
  return {
    description: project?.description || "",
    number: project?.number || "",
    customerId: project?.customer?.id ? String(project.customer.id) : "",
    defaultAddressId: project?.default_address?.id
      ? String(project.default_address.id)
      : "",
    managerIds: (project?.managers || []).map((manager) => String(manager.id)),
    dateStart: project?.date_start || "",
    dateEnd: project?.date_end || "",
  };
}

/**
 * Build the API payload shared by create and patch.
 *
 * @param {Object} draft - Modal draft state.
 * @returns {Object}
 */
function buildProjectPayload(draft) {
  return {
    description: draft.description,
    number: draft.number,
    customer_id: draft.customerId || null,
    default_address_id: draft.defaultAddressId || null,
    manager_ids: draft.managerIds.map(Number),
    date_start: draft.dateStart || null,
    date_end: draft.dateEnd || null,
  };
}

/**
 * Add / edit modal for project fields backed by the JobNumber model.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Whether the modal is visible.
 * @param {"edit"|"add"} [props.mode="edit"] - Form mode.
 * @param {Object|null} [props.project] - Project API row to edit (edit mode).
 * @param {(payload: Object) => Promise<Object>} props.onSubmit - Create or patch handler.
 * @param {() => void} props.onClose - Close without saving.
 * @param {(savedJob: Object) => void} [props.onSaved] - Called after a successful save.
 * @returns {JSX.Element|null}
 */
const ProjectFormModal = ({
  open,
  mode = "edit",
  project = null,
  onSubmit,
  onClose,
  onSaved,
}) => {
  const isAddMode = mode === "add";
  const [draft, setDraft] = useState(() => getInitialDraft(project));
  const [addresses, setAddresses] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [error, setError] = useState("");

  const showInactiveDatesWarning = !draft.dateStart && !draft.dateEnd;

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraft(getInitialDraft(isAddMode ? null : project));
    setError("");
  }, [open, project, isAddMode]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setIsLoadingOptions(true);
    Promise.allSettled([
      getAddresses(),
      getCustomers(),
      getUsersByRole({ role: "manager", active: true }),
    ])
      .then(([addressResult, customerResult, managerResult]) => {
        setAddresses(
          addressResult.status === "fulfilled" ? addressResult.value || [] : []
        );
        setCustomers(
          customerResult.status === "fulfilled" ? customerResult.value || [] : []
        );
        setManagers(
          managerResult.status === "fulfilled" ? managerResult.value || [] : []
        );
        if (
          addressResult.status === "rejected" ||
          customerResult.status === "rejected" ||
          managerResult.status === "rejected"
        ) {
          console.error("Failed to load one or more project form option lists", {
            addressResult,
            customerResult,
            managerResult,
          });
          setError("Some project form options could not be loaded.");
        }
      })
      .finally(() => setIsLoadingOptions(false));
  }, [open]);

  const managerOptions = useMemo(
    () =>
      managers.map((manager) => ({
        value: String(manager.id),
        label: getUserDisplayName(manager),
      })),
    [managers]
  );
  const addressOptions = useMemo(
    () =>
      addresses.map((address) => ({
        id: String(address.id),
        label: address.address,
      })),
    [addresses]
  );
  const customerOptions = useMemo(
    () =>
      customers.map((customer) => ({
        id: String(customer.id),
        label: customer.name,
      })),
    [customers]
  );

  /**
   * Patch one field on the project draft.
   *
   * @param {string} field Draft field key.
   * @param {*} value Next value.
   * @returns {void}
   */
  const updateDraft = (field, value) => {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  /**
   * Build the payload and call `onSubmit`, then `onSaved` with the saved job.
   *
   * @param {React.FormEvent<HTMLFormElement>} event Form submit event.
   * @returns {Promise<void>}
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSaving || (!isAddMode && !project)) {
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const savedJob = await onSubmit(buildProjectPayload(draft));
      onSaved?.(savedJob);
    } catch (requestError) {
      console.error(
        isAddMode ? "Failed to create project" : "Failed to save project",
        requestError
      );
      // Prefer backend message (e.g. 403 permission) over a generic fallback.
      const apiMessage =
        requestError?.response?.data?.error ||
        requestError?.response?.data?.message;
      setError(
        apiMessage ||
          (isAddMode
            ? "Project could not be created. Please try again."
            : "Project could not be saved. Please try again.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) {
    return null;
  }
  if (!isAddMode && !project) {
    return null;
  }

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      maxWidth={720}
      closeButton={false}
      className="relative w-full max-h-[90vh] overflow-y-auto rounded-lg bg-white p-0 shadow-lg"
      overlayClassName="z-[10050]"
      outsideClickIgnoreSelector="[data-searchable-multi-select-listbox], [data-searchable-dropdown-listbox]"
    >
      <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
        <div className="flex items-center justify-between gap-3 bg-primary px-4 py-3">
          <h2
            id={TITLE_ID}
            className="text-lg font-semibold leading-none tracking-tight text-white"
          >
            {isAddMode ? "Add Project" : "Edit Project"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="bg-gray-200 px-4 py-2">
          <p className="text-sm font-medium text-gray-700">
            {isAddMode
              ? "Create a new project from the fields below."
              : `${project?.number} - ${getProjectTitle(project)}`}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4">
          <div className="grid gap-4 md:grid-cols-[minmax(150px,0.35fr),1fr]">
            <label
              htmlFor="project-number"
              className="block text-sm font-medium text-gray-700"
            >
              Project number
              <input
                id="project-number"
                value={draft.number}
                onChange={(event) => updateDraft("number", event.target.value)}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
              />
            </label>

            <label
              htmlFor="project-description"
              className="block text-sm font-medium text-gray-700"
            >
              Name
              <input
                id="project-description"
                value={draft.description}
                onChange={(event) =>
                  updateDraft("description", event.target.value)
                }
                className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
              />
            </label>
          </div>

          <div>
            <label
              htmlFor="project-customer"
              className="block text-sm font-medium text-gray-700"
            >
              Owner / Customer
            </label>
            <SearchableDropdown
              options={customerOptions}
              value={draft.customerId}
              onChange={(customerId) =>
                updateDraft("customerId", customerId ? String(customerId) : "")
              }
              placeholder="Select customer"
              disabled={isLoadingOptions}
              className="mt-1"
              listboxClassName="z-[10060]"
            />
          </div>

          <div>
            <label
              htmlFor="project-address"
              className="block text-sm font-medium text-gray-700"
            >
              Address
            </label>
            <SearchableDropdown
              options={addressOptions}
              value={draft.defaultAddressId}
              onChange={(addressId) =>
                updateDraft(
                  "defaultAddressId",
                  addressId ? String(addressId) : ""
                )
              }
              placeholder="Select address"
              disabled={isLoadingOptions}
              className="mt-1 text-sm"
              inputClassName="h-[38px] rounded-lg px-3 text-sm focus:ring-secondary-100 focus:ring-4"
              listboxClassName="z-[10060]"
            />
          </div>

          <SearchableMultiSelect
            label="Project manager(s)"
            options={managerOptions}
            value={draft.managerIds}
            onChange={(values) => updateDraft("managerIds", values)}
            placeholder="Search managers..."
            emptyPlaceholder="No managers selected"
            disabled={isLoadingOptions}
            listboxClassName="z-[10060]"
          />

          <div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="project-date-start"
                  className="block text-sm font-medium text-gray-700"
                >
                  Target start
                </label>
                <input
                  id="project-date-start"
                  type="date"
                  value={draft.dateStart}
                  onChange={(event) =>
                    updateDraft("dateStart", event.target.value)
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
                />
              </div>

              <div>
                <label
                  htmlFor="project-date-end"
                  className="block text-sm font-medium text-gray-700"
                >
                  Target end
                </label>
                <input
                  id="project-date-end"
                  type="date"
                  value={draft.dateEnd}
                  onChange={(event) =>
                    updateDraft("dateEnd", event.target.value)
                  }
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
                />
              </div>
            </div>
            {showInactiveDatesWarning ? (
              <p className="mt-1.5 text-xs text-amber-700">
                Projects with no dates will not be active
              </p>
            ) : null}
          </div>

          {error ? (
            <p className="text-sm font-medium text-red-600" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <Button
              variant="custom"
              className="border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100"
              onClick={onClose}
              text="Cancel"
            />
            <Button
              variant="custom"
              className="bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-300"
              disabled={isSaving || isLoadingOptions}
              onClick={handleSubmit}
              text={
                isSaving
                  ? isAddMode
                    ? "Creating..."
                    : "Saving..."
                  : isAddMode
                    ? "Create project"
                    : "Save changes"
              }
            />
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default ProjectFormModal;
